import { apiClient } from './client';

/**
 * Client for `/api/files` — presigned direct-to-bucket uploads
 * (Foundation 1). Mirrors server/src/routes/files.routes.ts +
 * modules/files/files.service.ts.
 *
 * The upload itself never goes through this client: `presignUpload`
 * returns a short-lived `uploadUrl` the browser PUTs the bytes to
 * directly (see `useFileUpload`). This module only mints presigns and
 * reads/serves metadata.
 *
 * When storage is unconfigured (empty `S3_BUCKET`, the dev default) the
 * presign/confirm/download/delete endpoints respond 503 `storage_disabled`
 * — callers degrade gracefully. `listFilesForEntity` is not storage-gated
 * and returns `[]`.
 */

export type FileObjectStatus = 'pending' | 'ready';

/** Client-safe file metadata — the server never exposes the storage key. */
export interface FileAttachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  entityType: string | null;
  entityId: string | null;
  status: FileObjectStatus;
  uploadedByMembershipId: string | null;
  uploaderNameSnapshot: string;
  createdAt: string;
}

export interface PresignInput {
  filename: string;
  contentType: string;
  sizeBytes: number;
  entityType?: string | null;
  entityId?: string | null;
}

export interface PresignResult {
  fileId: string;
  /** Presigned PUT URL — the browser uploads bytes here directly. */
  uploadUrl: string;
}

export interface DownloadUrl {
  url: string;
  filename: string;
  contentType: string;
}

/** Server error code carried in `{ error }` when storage is disabled. */
export const STORAGE_DISABLED_CODE = 'storage_disabled';

// ── POST /api/files/presign ─────────────────────────────────────────────

export async function presignUpload(input: PresignInput): Promise<PresignResult> {
  const res = await apiClient.post<{ data: PresignResult }>('/api/files/presign', input);
  return res.data.data;
}

// ── POST /api/files/:id/confirm ─────────────────────────────────────────

export async function confirmUpload(fileId: string): Promise<FileAttachment> {
  const res = await apiClient.post<{ data: FileAttachment }>(
    `/api/files/${encodeURIComponent(fileId)}/confirm`,
  );
  return res.data.data;
}

// ── GET /api/files/:id/download-url ─────────────────────────────────────

export async function getDownloadUrl(fileId: string): Promise<DownloadUrl> {
  const res = await apiClient.get<{ data: DownloadUrl }>(
    `/api/files/${encodeURIComponent(fileId)}/download-url`,
  );
  return res.data.data;
}

// ── DELETE /api/files/:id ───────────────────────────────────────────────

export async function deleteFile(fileId: string): Promise<void> {
  await apiClient.delete(`/api/files/${encodeURIComponent(fileId)}`);
}

// ── GET /api/files?entityType=&entityId= ────────────────────────────────

export async function listFilesForEntity(entityType: string, entityId: string): Promise<FileAttachment[]> {
  const res = await apiClient.get<{ data: FileAttachment[] }>('/api/files', {
    params: { entityType, entityId },
  });
  return res.data.data;
}
